import { CANVAS_VIEW_ZOOM_MAX, CANVAS_VIEW_ZOOM_MIN } from '@/spaces/constants';
import type {
    Space,
    SpaceAssessment,
    SpaceAssessmentGap,
    SpaceCan,
    SpaceCanvasViewport,
    SpaceSeedItemPayload,
    SpaceNode,
    SpaceNodePayload,
    SpacePayload,
    SpaceSummary,
} from '@/spaces/types/types';

// ── HTTP errors ─────────────────────────────────────────────────────────────

/**
 * Pulls a readable message from axios-style errors or generic thrown values.
 *
 * @param err - Caught rejection (often `{ response: { data: { error } } }` from @pendo/http)
 * @returns Message suitable for re-throwing or showing to the user
 */
export function extractHttpErrorMessage (err: unknown): string {
    const axiosLike = err as { response?: { data?: { error?: string } }; message?: string };
    if (axiosLike.response?.data?.error) {
        return axiosLike.response.data.error;
    }
    if (err instanceof Error) {
        return err.message;
    }

    return String(err);
}

// ── Response: Node service → app types ─────────────────────────────────────

/**
 * Converts Firestore-style timestamps, ISO strings, or epoch numbers to milliseconds.
 *
 * @param value - Raw `createdAt` / `updatedAt` from the spaces API JSON
 * @returns Unix time in ms; falls back to `Date.now()` when the value is unknown
 */
function timestampToMs (value: unknown): number {
    if (typeof value === 'number' && !Number.isNaN(value)) {
        return value;
    }
    if (value && typeof value === 'object') {
        const v = value as Record<string, unknown>;
        if (typeof v.seconds === 'number') {
            return v.seconds * 1000;
        }
        if (typeof v._seconds === 'number') {
            return v._seconds * 1000;
        }
    }
    if (typeof value === 'string') {
        const parsed = Date.parse(value);
        if (!Number.isNaN(parsed)) {
            return parsed;
        }
    }

    return Date.now();
}

/**
 * Reads parent linkage from API JSON.
 *
 * @param raw - One node object from the spaces service
 * @returns Parent id string, or `undefined` when absent
 */
function parentItemIdFromBackend (raw: Record<string, unknown>): string | undefined {
    if (raw.parentItemId != null) {
        return String(raw.parentItemId);
    }

    return undefined;
}

/**
 * When the API omits `isBacklog`, infer placement: nodes with both `x` and `y` are on the canvas;
 * otherwise treat as parking lot. Explicit `true` / `false` always wins.
 */
function isBacklogFromBackend (raw: Record<string, unknown>): boolean {
    if (raw.isBacklog != null) {
        return Boolean(raw.isBacklog);
    }

    const onCanvas = raw.x != null && raw.y != null;

    return !onCanvas;
}

/**
 * Maps a single space node document from the Node/Firestore API into app `SpaceNode` shape.
 *
 * @param raw - Parsed JSON object for one node
 * @returns Normalized node for Vue / vue-flow
 */
export function normalizeNodeFromBackend (raw: Record<string, unknown>): SpaceNode {
    return {
        id: String(raw.id),
        name: String(raw.name ?? ''),
        description: String(raw.description ?? ''),
        kind: String(raw.kind ?? 'artifact'),
        type: 'artifact',
        data: (raw.data as Record<string, unknown>) ?? {},
        parentItemId: parentItemIdFromBackend(raw),
        isBacklog: isBacklogFromBackend(raw),
        isLive: Boolean(raw.isLive),
        height: Number(raw.height ?? 0),
        width: Number(raw.width ?? 0),
        x: raw.x != null ? Number(raw.x) : undefined,
        y: raw.y != null ? Number(raw.y) : undefined,
    };
}

/**
 * Normalizes a summary object from the backend into a `SpaceSummary`.
 *
 * @param raw - The `summary` field from the space JSON (may be null/undefined/object)
 * @returns Normalized summary or `undefined` when absent
 */
function normalizeSummaryFromBackend (raw: unknown): SpaceSummary | undefined {
    if (!raw || typeof raw !== 'object') return undefined;

    const obj = raw as Record<string, unknown>;
    if (!obj.content) return undefined;

    return {
        content: String(obj.content),
        updatedBy: String(obj.updatedBy ?? ''),
        updatedAt: timestampToMs(obj.updatedAt),
    };
}

/**
 * Normalizes an assessment object from the backend into a `SpaceAssessment`.
 *
 * @param raw - The `assessment` field from the space JSON (may be null/undefined/object)
 * @returns Normalized assessment or `undefined` when absent
 */
function normalizeAssessmentFromBackend (raw: unknown): SpaceAssessment | undefined {
    if (!raw || typeof raw !== 'object') return undefined;

    const obj = raw as Record<string, unknown>;
    if (typeof obj.rating !== 'number') return undefined;

    const gaps: SpaceAssessmentGap[] = Array.isArray(obj.gaps)
        ? (obj.gaps as Record<string, unknown>[])
            .filter((gap) => gap && typeof gap === 'object' && typeof gap.content === 'string')
            .map((gap) => ({
                content: String(gap.content),
                importance: ([1, 2, 3].includes(Number(gap.importance)) ? Number(gap.importance) : 1) as 1 | 2 | 3,
            }))
        : [];

    return {
        rating: obj.rating,
        gaps,
        numberOfItemsUsed: typeof obj.numberOfItemsUsed === 'number' ? obj.numberOfItemsUsed : 0,
        updatedAt: timestampToMs(obj.updatedAt),
        updatedBy: String(obj.updatedBy ?? ''),
    };
}

/**
 * Maps a space document from the Node service into the frontend `Space` model.
 * Adds `edges: []` because the backend does not return edges yet.
 *
 * @param raw - Parsed JSON object for one space
 * @returns Space with numeric `createdAt` / `lastUpdatedAt` for the UI
 */
function thumbnailUrlFromRaw (raw: Record<string, unknown>): string | undefined {
    const candidate =
        raw.thumbnailUrl ??
        raw.ThumbnailUrl ??
        raw.thumbnail_url;
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
        return candidate.trim();
    }

    return undefined;
}

/** Only include `isStarred` when the API sends a boolean so omitted metadata does not imply false. */
function isStarredFromRaw (raw: Record<string, unknown>): { isStarred: boolean } | Record<string, never> {
    if (raw.isStarred === true) {
        return { isStarred: true };
    }
    if (raw.isStarred === false) {
        return { isStarred: false };
    }

    return {};
}

/**
 * Parses a canvas viewport object from API JSON (`canvas-view` response or `viewerCanvasViewport`).
 * Returns `null` when the payload is missing, not an object, or has non-finite coordinates/zoom.
 */
export function parseSpaceCanvasViewportFromUnknown (data: unknown): SpaceCanvasViewport | null {
    if (data == null) {
        return null;
    }
    if (typeof data !== 'object') {
        return null;
    }
    const o = data as Record<string, unknown>;
    const zoom = Number(o.zoom);
    const x = Number(o.x);
    const y = Number(o.y);
    if (!Number.isFinite(zoom) || !Number.isFinite(x) || !Number.isFinite(y)) {
        return null;
    }

    return {
        zoom: Math.min(CANVAS_VIEW_ZOOM_MAX, Math.max(CANVAS_VIEW_ZOOM_MIN, zoom)),
        x,
        y,
    };
}

/**
 * Parses the spaces API `can` object. Returns `undefined` if absent or not five booleans.
 *
 * @param raw - Top-level space payload from the backend.
 * @returns A strict `SpaceCan` or `undefined` when the payload is not usable.
 */
function spaceCanFromRaw (raw: Record<string, unknown>): SpaceCan | undefined {
    const c = raw.can;
    if (c == null || typeof c !== 'object') {
        return undefined;
    }
    const o = c as Record<string, unknown>;
    const { owner, edit, add, share, delete: del } = o;
    if (
        typeof owner !== 'boolean'
        || typeof edit !== 'boolean'
        || typeof add !== 'boolean'
        || typeof share !== 'boolean'
        || typeof del !== 'boolean'
    ) {
        return undefined;
    }

    return { owner, edit, add, share, delete: del };
}

/**
 * Parses `viewerCanvasViewport` from the spaces API. Returns `undefined` if the key is absent
 * (merge with cached space), `null` when explicitly none, or a clamped viewport object.
 */
function viewerCanvasViewportFromRaw (
    raw: Record<string, unknown>,
): SpaceCanvasViewport | null | undefined {
    if (!Object.prototype.hasOwnProperty.call(raw, 'viewerCanvasViewport')) {
        return undefined;
    }

    return parseSpaceCanvasViewportFromUnknown(raw.viewerCanvasViewport);
}

export function normalizeSpaceFromBackend (raw: Record<string, unknown>): Space {
    const nodes = Array.isArray(raw.nodes)
        ? raw.nodes.map((rawNode) => normalizeNodeFromBackend(rawNode as Record<string, unknown>))
        : [];

    const viewerCanvasViewport = viewerCanvasViewportFromRaw(raw);
    const can = spaceCanFromRaw(raw);

    const signals = Array.isArray(raw.signals)
        ? (raw.signals as unknown[]).filter((s): s is string => typeof s === 'string')
        : undefined;

    return {
        id: String(raw.id),
        name: String(raw.name ?? ''),
        description: String(raw.description ?? ''),
        ...(typeof raw.primaryTarget === 'string' ? { primaryTarget: raw.primaryTarget } : {}),
        ...(typeof raw.purpose === 'string' ? { purpose: raw.purpose } : {}),
        ...(signals !== undefined ? { signals } : {}),
        summary: normalizeSummaryFromBackend(raw.summary),
        assessment: normalizeAssessmentFromBackend(raw.assessment),
        visibility: raw.visibility === 'public' ? 'public' : 'private',
        ownerUserId:
            raw.ownerUserId != null && String(raw.ownerUserId).length > 0
                ? String(raw.ownerUserId)
                : '',
        ...(can != null ? { can } : {}),
        thumbnailUrl: thumbnailUrlFromRaw(raw),
        thumbnailViaSpacesApi: raw.thumbnailViaSpacesApi === true,
        ...isStarredFromRaw(raw),
        ...(viewerCanvasViewport !== undefined ? { viewerCanvasViewport } : {}),
        nodes,
        edges: [],
        createdAt: timestampToMs(raw.createdAt),
        lastUpdatedAt: timestampToMs(raw.updatedAt ?? raw.lastUpdatedAt),
    };
}
// ── Request: app types → Node service ──────────────────────────────────────

/**
 * Builds the JSON body for creating or embedding a node in the Node service.
 * Fills `x`/`y` defaults and `appIds: []` expected by the backend.
 *
 * @param node - Payload from the app; may include optional `id` when preserving backlog ids
 * @returns Plain object ready for `JSON` POST bodies
 */
export function toBackendNodeBody (node: SpaceNodePayload & { id?: string }): Record<string, unknown> {
    const body: Record<string, unknown> = {
        name: node.name,
        kind: node.kind,
        type: node.type,
        data: node.data ?? {},
        isBacklog: node.isBacklog,
        isLive: node.isLive,
        height: node.height,
        width: node.width,
        x: node.x ?? 0,
        y: node.y ?? 0,
        appIds: [],
    };
    if (node.parentItemId != null) {
        body.parentItemId = node.parentItemId;
    }
    if (node.id != null) {
        body.id = node.id;
    }

    return body;
}

/**
 * One entry for POST `/spaces` `items` — mirrors item fields accepted by spaces-backend.
 */
export function toBackendItemBody (item: SpaceSeedItemPayload): Record<string, unknown> {
    const body: Record<string, unknown> = {
        kind: item.kind,
        type: item.type,
        data: item.data ?? {},
        isBacklog: item.isBacklog ?? true,
        isLive: item.isLive ?? false,
        x: item.x ?? 0,
        y: item.y ?? 0,
        width: item.width ?? 500,
        height: item.height ?? 400,
        appIds: [],
    };
    if (item.name !== undefined) {
        body.name = item.name;
    }
    if (item.title !== undefined) {
        body.title = item.title;
    }
    if (item.description !== undefined) {
        body.description = item.description;
    }
    if (item.parentItemId != null && item.parentItemId !== '') {
        body.parentItemId = item.parentItemId;
    }

    return body;
}

/**
 * Builds a partial update body for `PUT /spaces/:id`. Only defined fields are sent.
 * Omits `edges` (not stored by the Node service).
 *
 * @param updates - Subset of `SpacePayload` fields to change
 * @returns Plain object for the request body (may be empty if nothing to send)
 */
export function toBackendUpdateBody (updates: Partial<SpacePayload>): Record<string, unknown> {
    const body: Record<string, unknown> = {};
    if (updates.name !== undefined) {
        body.name = updates.name;
    }
    if (updates.description !== undefined) {
        body.description = updates.description;
    }
    if (updates.visibility !== undefined) {
        body.visibility = updates.visibility;
    }
    if (updates.primaryTarget !== undefined) {
        body.primaryTarget = updates.primaryTarget;
    }
    if (updates.purpose !== undefined) {
        body.purpose = updates.purpose;
    }
    if (updates.signals !== undefined) {
        body.signals = updates.signals;
    }
    if (updates.nodes !== undefined) {
        body.nodes = updates.nodes.map((node) => toBackendNodeBody(node));
    }

    return body;
}
